import { memo, useMemo, useRef, useState } from "react"
import { useVirtualizer } from "@tanstack/react-virtual"
import {
	IconArrowTurnRightOutline18 as RedirectIcon,
	IconMagnifierOutline18 as SearchIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import { Dialog, Empty, IconButton, Input, Tooltip } from "@productlane/ui"

import type { FolderRedirect, FolderRedirectItem } from "./folder-redirects"
import { useRole } from "@/lib/useRole"
import { groupRedirects, matchesRedirect } from "./folder-redirects"
import { useRemoveRedirect } from "./use-remove-redirect"

const REDIRECT_HEIGHT = 28
const ARTICLE_HEIGHT = 28
const ARTICLE_GAP = 12

const itemHeight = (item: FolderRedirectItem | undefined, index: number) =>
	item?.kind === "article"
		? ARTICLE_HEIGHT + (index === 0 ? 0 : ARTICLE_GAP)
		: REDIRECT_HEIGHT

export const FolderRedirectsDialog = memo(
	({
		groupName,
		redirects,
		isOpen,
		onClose,
	}: {
		groupName: string
		redirects: readonly FolderRedirect[]
		isOpen: boolean
		onClose: () => void
	}) => {
		const { role } = useRole()
		const canEdit = !!role && role !== "VIEWER"
		const removeRedirect = useRemoveRedirect()
		const [query, setQuery] = useState("")
		const items = useMemo(
			() =>
				groupRedirects(redirects.filter((row) => matchesRedirect(row, query))),
			[redirects, query],
		)
		const scrollRef = useRef<HTMLDivElement>(null)
		// Each row carries a tooltip, so a long history renders only what is on screen.
		const virtualizer = useVirtualizer({
			count: items.length,
			getScrollElement: () => scrollRef.current,
			estimateSize: (index) => itemHeight(items[index], index),
			getItemKey: (index) => items[index]?.key ?? index,
			overscan: 8,
			initialRect: { width: 560, height: 440 },
		})

		return (
			<Dialog
				size="lg"
				isOpen={isOpen}
				onOpenChange={(open) => {
					if (!open) {
						setQuery("")
						onClose()
					}
				}}
				title="Redirects"
				description={`Old URLs that open articles in ${groupName}.`}
			>
				{redirects.length === 0 ? (
					<Empty
						image={<RedirectIcon className="h-8 w-8 text-gray-400" />}
						title="Every URL is current"
						text="Renaming the group or a published article's URL adds a redirect here."
						className="py-10"
					/>
				) : (
					<div className="flex flex-col gap-3">
						<Input
							name="redirect-search"
							icon={SearchIcon}
							placeholder="Search"
							value={query}
							onChange={(event) => setQuery(event.target.value)}
						/>
						<div
							ref={scrollRef}
							className="-mx-1 max-h-[55dvh] overflow-y-auto pb-2"
						>
							<ul
								className="relative list-none"
								style={{ height: `${virtualizer.getTotalSize()}px` }}
							>
								{virtualizer.getVirtualItems().map((virtual) => {
									const item = items[virtual.index]
									if (!item) return null
									const position = {
										height: `${virtual.size}px`,
										transform: `translateY(${virtual.start}px)`,
									}
									if (item.kind === "article") {
										return (
											<li
												key={item.key}
												className="absolute left-0 top-0 flex w-full items-end gap-3 px-3 pb-1"
												style={position}
											>
												<span className="min-w-0 truncate text-sm font-medium text-primary">
													{item.title}
												</span>
												<span
													className="ml-auto min-w-0 truncate font-mono text-xs text-tertiary"
													title={`/docs/${item.to}`}
												>
													/docs/{item.to}
												</span>
											</li>
										)
									}
									const { row } = item
									return (
										<li
											key={item.key}
											data-testid="folder-redirect-row"
											className="group/row absolute left-0 top-0 flex w-full items-center gap-2 rounded-md pl-3 pr-1 hover:bg-gray-100 dark:hover:bg-gray-800"
											style={position}
										>
											<span
												className="min-w-0 flex-1 truncate font-mono text-xs"
												title={`/docs/${row.from}`}
											>
												<span className="text-tertiary">/docs/</span>
												<span className="text-secondary">{row.from}</span>
											</span>
											{canEdit && (
												<Tooltip label="Remove redirect">
													<IconButton
														Icon={XIcon}
														variant="ghost"
														size="sm"
														iconClassName="h-3 w-3"
														aria-label="Remove redirect"
														className="flex-none cursor-default opacity-0 focus-visible:opacity-100 group-hover/row:opacity-100"
														onClick={() =>
															void removeRedirect(row.articleId, row.from)
														}
													/>
												</Tooltip>
											)}
										</li>
									)
								})}
							</ul>
						</div>
						{items.length === 0 && (
							<p className="pb-6 pt-2 text-center text-sm text-secondary">
								{`No redirects match "${query.trim()}".`}
							</p>
						)}
					</div>
				)}
			</Dialog>
		)
	},
)
FolderRedirectsDialog.displayName = "FolderRedirectsDialog"
